/**
 * OverflowToolbar — a horizontal button row that collapses its RIGHTMOST items
 * into a "=" dropdown when there isn't enough width. The available width is
 * supplied by the caller (`availableWidth`) so a parent can arbitrate space
 * between this and a sibling (e.g. a breadcrumb that gets priority); without it
 * the toolbar measures its own box.
 *
 * Items render once in a hidden measurement row (for their natural widths) and
 * again either inline (visible) or in the overflow popover — so each control
 * keeps its own state regardless of where it shows. The leftmost items survive
 * longest; the popover is chrome-less (the items carry their own pill styling)
 * and right-aligned under the "=" trigger.
 */
import {
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { ChevronsRight } from "lucide-react";
import { IconButton, Tooltip } from "@archlang/ui";

export interface OverflowItem {
  readonly key: string;
  readonly node: ReactNode;
}

const GAP = 8; // matches gap-2
const MENU_BTN = 40; // reserved width for the "=" trigger

export function OverflowToolbar({
  items,
  availableWidth,
  className,
}: {
  readonly items: readonly OverflowItem[];
  /** Width budget for the inline row (px). Omit to measure the toolbar's box. */
  readonly availableWidth?: number;
  readonly className?: string;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [count, setCount] = useState(items.length);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  useLayoutEffect(() => {
    const meas = measureRef.current;
    const row = rowRef.current;
    if (!meas) return;
    const recompute = (): void => {
      const avail = availableWidth ?? row?.clientWidth ?? 0;
      const widths = [...meas.children].map((c) => (c as HTMLElement).offsetWidth);
      let used = 0;
      let n = 0;
      for (let i = 0; i < widths.length; i++) {
        const next = used + (i > 0 ? GAP : 0) + widths[i];
        const moreAfter = i < widths.length - 1;
        if (next + (moreAfter ? GAP + MENU_BTN : 0) <= avail) {
          used = next;
          n++;
        } else break;
      }
      setCount(n);
    };
    const ro = new ResizeObserver(recompute);
    ro.observe(meas);
    if (row && availableWidth === undefined) ro.observe(row);
    recompute();
    return () => ro.disconnect();
  }, [items, availableWidth]);

  // Close the popover on outside click / Escape / resize.
  useLayoutEffect(() => {
    if (!open) return;
    const close = (): void => setOpen(false);
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("click", close);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const visible = items.slice(0, count);
  const overflow = items.slice(count);

  return (
    <div
      ref={rowRef}
      style={availableWidth !== undefined ? { maxWidth: availableWidth } : undefined}
      className={
        "relative flex items-center justify-end gap-2 overflow-hidden " + (className ?? "")
      }
    >
      {/* Hidden measurement row — natural widths for the fit calculation. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute left-0 top-0 flex items-center gap-2"
      >
        {items.map((it) => (
          <div key={it.key} className="flex items-center gap-2">
            {it.node}
          </div>
        ))}
      </div>

      {visible.map((it) => (
        <div key={it.key} className="flex shrink-0 items-center gap-2">
          {it.node}
        </div>
      ))}

      {overflow.length > 0 && (
        <Tooltip content="More board actions">
          <IconButton
            aria-label="More board actions"
            onClick={(e) => {
              e.stopPropagation();
              const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
              setPos({ x: r.right, y: r.bottom + 6 });
              setOpen((o) => !o);
            }}
            // Board-floating HUD skin (border + blur + shadow) is layout, not
            // button paint — the kit owns size/rounding/focus/hover.
            className="w-9 shrink-0 border border-slate-700 bg-slate-900/(--tint-strong) shadow-lg backdrop-blur-md"
          >
            <ChevronsRight className="h-4 w-4" aria-hidden="true" />
          </IconButton>
        </Tooltip>
      )}

      {open &&
        overflow.length > 0 &&
        createPortal(
          <div
            role="menu"
            onClick={(e) => e.stopPropagation()}
            // Chrome-less: no panel background — the items carry their own pill
            // styling. Anchored under the "=" and right-aligned to it.
            style={{ top: pos.y, right: Math.max(8, window.innerWidth - pos.x) }}
            className="fixed z-overlay flex flex-col items-end gap-2"
          >
            {overflow.map((it) => (
              // Stack multi-control items (e.g. Color + Web) one per line.
              <div key={it.key} className="flex flex-col items-end gap-2">
                {it.node}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
